// ---------------------------------------------------------------------------
// DnD Email Editor – helpers for reading stored models
//
// Persisted DnD models arrive as `unknown` from several places (marketing
// settings' jsonHeader/jsonFooter, an email component's jsonContent, a
// template's json_content) and each caller needs one of two things: the whole
// model to open in the editor, or just its layers to offer as a saved block.
// Both used to be duplicated per page — they live here so they can't drift.
// ---------------------------------------------------------------------------

import type { DndEmailModel, Layer } from './dnd-model';
import { migrateModel } from './dnd-parser';
import { createEmptyModel } from './dnd-defaults';

/** True when `raw` looks like a stored DnD model (i.e. it has a layers array). */
function isStoredModel(raw: unknown): raw is { layers: Layer[] } {
  return (
    !!raw &&
    typeof raw === 'object' &&
    Array.isArray((raw as { layers?: unknown }).layers)
  );
}

/**
 * Coerce a stored value into an editable model, migrating older editorVersions.
 * Anything unrecognized (null, a bare string, a half-written row) becomes a
 * fresh empty model rather than throwing.
 */
export function toDndModel(raw: unknown): DndEmailModel {
  if (isStoredModel(raw)) {
    return migrateModel(raw as unknown as Record<string, unknown>);
  }
  return createEmptyModel();
}

/**
 * Pull just the layers out of a stored model, for use as a saved block. The
 * model's own globalStyles / wrapper / preheader are deliberately dropped — a
 * saved block is inserted into a host email and inherits the host's styling.
 * Returns undefined when there is nothing insertable.
 */
export function extractLayers(model: unknown): Layer[] | undefined {
  return isStoredModel(model) ? model.layers : undefined;
}
